// src/hooks/useFetchUsage.js
// Single source of truth for the daily manual-fetch counter on the frontend.
// Every page that shows "X / Y fetches" should use this hook so the numbers
// stay consistent across Overview / Publisher / Ecommerce.
//
// Reads BOTH `fetch_count` and `count` columns and takes the max — see
// supabase/2026-06-13-fetch-usage-columns.sql for why both exist.

import { useState, useEffect, useCallback } from 'react';
import { useAuth, useAppActive } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

const todayUtc = () => new Date().toISOString().split('T')[0];

export function useFetchUsage() {
  const { user, profile, isPro, isAdmin } = useAuth();
  const [usage, setUsage] = useState({
    used: 0,
    limit: 5,
    remaining: 5,
    lastFetchAt: null,
    loading: true,
  });

  const limit = isAdmin || isPro || profile?.role === 'admin' || profile?.role === 'superadmin' ? 10 : 5;

  const refresh = useCallback(async () => {
    if (!user?.id) return;

    // Try fetch_count + count; fall back to whichever exists.
    let row = null;
    try {
      let { data, error } = await supabase
        .from('fetch_usage')
        .select('fetch_count, count, last_fetch_at')
        .eq('user_id', user.id)
        .eq('date', todayUtc())
        .maybeSingle();

      if (error && /column .* does not exist|undefined column/i.test(error.message || '')) {
        const tryFetchCount = await supabase
          .from('fetch_usage')
          .select('fetch_count, last_fetch_at')
          .eq('user_id', user.id)
          .eq('date', todayUtc())
          .maybeSingle();
        if (!tryFetchCount.error) {
          row = tryFetchCount.data;
        } else {
          const tryCount = await supabase
            .from('fetch_usage')
            .select('count, last_fetch_at')
            .eq('user_id', user.id)
            .eq('date', todayUtc())
            .maybeSingle();
          row = tryCount.data;
        }
      } else {
        row = data;
      }
    } catch (e) {
      console.warn('[useFetchUsage] read failed:', e.message);
    }

    const used = Math.max(row?.fetch_count ?? 0, row?.count ?? 0);
    setUsage({
      used,
      limit,
      remaining: Math.max(0, limit - used),
      lastFetchAt: row?.last_fetch_at || null,
      loading: false,
    });
  }, [user?.id, limit]);

  useEffect(() => { refresh(); }, [refresh]);

  // Refresh when the tab regains focus — keeps Overview/Publisher/Ecommerce
  // in sync when the user kicks off a fetch from one and switches to another.
  useAppActive(refresh);

  return { ...usage, refresh };
}

export default useFetchUsage;
